import { SolarPanels_CalculationResult } from './types';

export class SolarPanelsQueryProvider {
  public validateAddressVariables(
    postcode: string,
    houseNumber: number,
    houseNumberAddition?: string
  ): object {
    return {
      postalCode: postcode,
      houseNumber,
      houseNumberAddition,
    };
  }

  public roofLocationMapping = {
    zuiden: 'V_SOUTH',
    zuidoost: 'V_SOUTHEAST',
    zuidwest: 'V_SOUTHWEST',
    westen: 'V_WEST',
    oosten: 'V_EAST',
    noorden: 'V_NORTH',
    noordoost: 'V_NORTHEAST',
    noordwest: 'V_NORTHWEST',
  };

  roofLocationPowerFactorMapping = {
    zuiden: 0.95,
    zuidoost: 0.87,
    zuidwest: 0.9,
    westen: 0.82,
    oosten: 0.8,
    noorden: 0.6,
    noordoost: 0.7,
    noordwest: 0.7,
  };

  public validateSolarPanelsVariables(
    searchCriteria: object,
    _selectedFilters: object,
    credentials: object,
    sessionId: string
  ): object {
    const products = JSON.parse(searchCriteria['products']);
    let additionalRoofSurfaceCost = [];
    let additionalMeterBoxCost = [];

    if (searchCriteria['roofSurface'] > 1) {
      additionalRoofSurfaceCost = products
        .filter((el) => [80409, 103764, 105422].includes(el.id))
        .map((el) => {
          return {
            description: `${
              Number(searchCriteria['roofSurface']) - 1
            }x oplevering meerdere dakvlakken`,
            calculationType: 'onetime',
            vatType: 'inclusive',
            vatPercentage: searchCriteria['vatValue'],
            calculationValue:
              (Number(searchCriteria['roofSurface']) - 1) * Number(el.price),
            supplierId: el.associatedSuppliers[0],
          };
        });
    }

    if (searchCriteria['meterBox'] === 'stoppen') {
      additionalMeterBoxCost = products
        .filter((el) => [80410, 103768, 105426].includes(el.id))
        .map((el) => {
          return {
            description: `Vervangen meterkast`,
            calculationType: 'onetime',
            vatType: 'inclusive',
            vatPercentage: searchCriteria['vatValue'],
            calculationValue: Number(el.price),
            supplierId: el.associatedSuppliers[0],
          };
        });
    }

    return {
      input: {
        request: {
          commonTechnical: [
            {
              sessionId,
              senderId: credentials['clientId'],
            },
          ],
          policyPolicyDetails: {
            objectBuildings: [
              {
                addresses: {
                  postalCode: searchCriteria['postcode'],
                  houseNumber: Number(searchCriteria['housenumber']),
                  houseNumberAddition: searchCriteria['housenumberaddition'],
                },
                roofConstructionType:
                  searchCriteria['roofType'] === 'platdak' ? 'V01' : 'V02',
                roofOrientation:
                  searchCriteria['roofType'] === 'platdak'
                    ? null
                    : this.roofLocationMapping[searchCriteria['roofLocation']],
              },
            ],
            salesDockComparisons: [
              {
                numberOfPanels: Number(searchCriteria['numberOfSolarPanels']),
                usageElectricity: Number(searchCriteria['electricityusage']),
                powerFactor:
                  searchCriteria['roofType'] === 'platdak'
                    ? 1
                    : this.roofLocationPowerFactorMapping[
                        searchCriteria['roofLocation']
                      ],
                additionalCosts: [
                  ...additionalRoofSurfaceCost,
                  ...additionalMeterBoxCost,
                ],
                hasShadowFormation:
                  searchCriteria['roofShade'] === '1' ? true : false,
              },
            ],
          },
        },
      },
    };
  }

  public calculateProductCosts(
    searchCriteria: object,
    supplierExternalId: any
  ): object {
    const products = JSON.parse(searchCriteria['products']);
    const supplierAdditionalCosts = JSON.parse(
      searchCriteria['supplierAdditionalCosts']
    );

    const additionalCosts = supplierAdditionalCosts.filter(
      (el) => el.supplierId === Number(supplierExternalId)
    )[0];

    const additionalCostPrices = {
      extraHighRoofPrice: products.filter(
        (el) => el.id === additionalCosts?.extraHighRoofProductId
      )[0]?.price,
      specialPlacePrice: products.filter(
        (el) => el.id === additionalCosts?.specialPlaceProductId
      )[0]?.price,
      removalOfSolarPanelsPrice: products.filter(
        (el) => el.id === additionalCosts?.removalOfSolarPanelsProductId
      )[0]?.price,
    };

    return additionalCostPrices;
  }

  public validateSuppliersVariables(
    result: SolarPanels_CalculationResult
  ): object {
    const productKrns = result.policyPolicyDetails?.map((item) => {
      return item.productCode;
    });

    return {
      productKrns: productKrns,
      productCategory: 'SOLAR_PANELS',
    };
  }

  public validateCreateLeadVariables(
    result: SolarPanels_CalculationResult,
    searchCriteria: object,
  ): object {

    return {
      input: {
        comparisonId: result?.commonTechnical?.[0]?.transactionReference,
        request: {
          partyPolicyHolders: [{
            callName: searchCriteria['name'],
            email: searchCriteria['email'],
            mobile: searchCriteria['telephone'],
            surname: searchCriteria['surname'],
            addresses: [{
              postalCode: searchCriteria['postcode'],
              houseNumber: Number(searchCriteria['housenumber']),
            }]
          }],
          salesDockLeads: [{
            numberOfPanels: Number(searchCriteria['numberOfSolarPanels']),
            usageElectricity: Number(searchCriteria['electricityusage']),
            roofOrientation: searchCriteria['roofType'] === 'platdak'
              ? null
              : this.roofLocationMapping[searchCriteria['roofLocation']],
            hasShadowFormation: searchCriteria['roofShade'] === '1',
            optIn: true,
            roofConstructionType:
              searchCriteria['roofType'] === 'platdak' ? 'V01' : 'V02',
            replaceFuseBox: searchCriteria['meterBox'] === 'stoppen',
          }]
        }
      }
    };
  }

  public validateEmailVariables(
    comparisonId: string | number,
    email: string,
    productType: string
  ): object {
    return {
      comparisonId,
      email,
      productType,
    };
  }
}
